import { useEffect, useRef, useState } from "react";
import * as Dialog from "@radix-ui/react-dialog";
import { ChevronDown, X } from "lucide-react";

import { cn } from "@/lib/cn";
import { Icon } from "@/lib/Icon";
import type { PlaybackGuide } from "../selfInjectionGuide";
import { GuideStepMedia } from "./GuideStepMedia";

interface StepModuleProps {
  guide: PlaybackGuide;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function StepModule({ guide, open, onOpenChange }: StepModuleProps) {
  const storageKey = `system.portal.guide.${guide.slug}${guide.progressVersion ? `.${guide.progressVersion}` : ""}`;
  const [i, setI] = useState(() => {
    try {
      const n = Number(sessionStorage.getItem(storageKey));
      return Number.isInteger(n) && n >= 0 && n < guide.steps.length ? n : 0;
    } catch {
      return 0;
    }
  });
  const [showTranscript, setShowTranscript] = useState(false);
  const bodyRef = useRef<HTMLDivElement>(null);
  const nextButtonRef = useRef<HTMLButtonElement>(null);
  const steps = guide.steps;
  const total = steps.length;
  const step = steps[i];

  // Restore before mounting media, so the wrong step never starts speaking.
  useEffect(() => {
    try {
      if (open) sessionStorage.setItem(storageKey, String(i));
    } catch {
      /* Guide remains usable when browser storage is unavailable. */
    }
  }, [i, open, storageKey]);
  useEffect(() => {
    if (bodyRef.current) bodyRef.current.scrollTop = 0;
  }, [i]);

  const prev = () => {
    // Back disappears on step one; keep keyboard focus in the navigation row.
    if (i === 1) nextButtonRef.current?.focus();
    setI((n) => Math.max(0, n - 1));
  };
  const next = () => {
    if (i === total - 1) {
      try {
        sessionStorage.removeItem(storageKey);
      } catch {
        /* optional storage */
      }
      onOpenChange(false);
    } else {
      setI((n) => Math.min(total - 1, n + 1));
    }
  };

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal forceMount>
        <Dialog.Overlay
          forceMount
          className={cn(
            "fixed inset-0 bg-black/10 backdrop-blur-[10px] z-dialog",
            "transition-opacity duration-base ease-ease",
            "data-[state=closed]:opacity-0 data-[state=open]:opacity-100",
            "data-[state=closed]:pointer-events-none",
          )}
        />
        <Dialog.Content
          forceMount
          className={cn(
            "fixed inset-x-0 bottom-0 max-h-[calc(100dvh-45px)] md:inset-auto md:top-1/2 md:left-1/2",
            "md:w-[min(48rem,calc(100vw-40px))] md:max-h-[min(calc(100dvh-40px),84rem)]",
            "overflow-hidden",
            "bg-page text-ink",
            "flex flex-col",
            "z-dialog",
            // Mobile: slide up + fade. Desktop: opacity only (no slide,
            // stays centered via md:-translate-y-1/2 in both states).
            // pointer-events-none while closed so the forceMount'd
            // content can't intercept clicks on whatever's behind.
            "transition-[transform,opacity] duration-base ease-ease",
            "data-[state=closed]:translate-y-full data-[state=open]:translate-y-0",
            "data-[state=closed]:opacity-0 data-[state=open]:opacity-100",
            "data-[state=closed]:pointer-events-none",
            "md:-translate-x-1/2",
            "md:data-[state=closed]:-translate-y-1/2 md:data-[state=open]:-translate-y-1/2",
          )}
          aria-describedby={undefined}
        >
          {/* Top bar */}
          <header className="flex items-center justify-between border-b border-line-subtle shrink-0 p-2">
            <Dialog.Title className="font-mono uppercase text-13 text-ink-secondary px-15 flex-1">
              {guide.title.toUpperCase()}
            </Dialog.Title>
            <Dialog.Close
              aria-label="Close guide"
              className="w-40 h-40 flex items-center justify-center text-[#5E5E5E] hover:text-ink bg-slate rounded-sm"
            >
              <X size={16} strokeWidth={2} />
            </Dialog.Close>
          </header>

          {/* Scrollable body */}
          <div ref={bodyRef} className="min-h-0 flex-auto overflow-y-auto">
            {open && step && <GuideStepMedia step={step} />}

            {/* Step copy */}
            <div className="px-15 md:px-25 pt-25 pb-15 flex flex-col gap-15">
              <div
                className="type-label-sm text-ink-secondary"
                aria-live="polite"
              >
                STEP {step?.index ?? "—"} OF {total}
              </div>
              <h2 className="type-title-lg">{step?.title ?? ""}</h2>
              <p className="type-body-md text-ink-secondary">
                {step?.body ?? ""}
              </p>
              {step?.note && (
                <aside className="border border-line-subtle bg-raised p-15 flex flex-col gap-10">
                  <h3 className="type-label-sm">{step.note.title}</h3>
                  <p className="type-body-md text-ink-secondary">
                    {step.note.body}
                  </p>
                  <a
                    href={step.note.link.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="type-body-md text-ink underline underline-offset-2 min-h-45 inline-flex items-center self-start"
                  >
                    {step.note.link.label}
                    <span className="sr-only"> (opens in a new tab)</span>
                  </a>
                </aside>
              )}
            </div>

            {/* Transcript */}
            {step?.transcript ? (
              <section className="border-t border-line-subtle">
                <button
                  type="button"
                  onClick={() => setShowTranscript((v) => !v)}
                  className="w-full flex items-center justify-between px-15 md:px-25 h-40 type-body-md text-ink hover:text-ink"
                  aria-expanded={showTranscript}
                >
                  <span>
                    {showTranscript ? "Hide Transcript" : "Show Transcript"}
                  </span>
                  <ChevronDown
                    size={16}
                    strokeWidth={1.5}
                    className={cn(
                      "transition-transform duration-fast",
                      showTranscript && "rotate-180",
                    )}
                  />
                </button>
                {showTranscript && (
                  <div className="px-15 md:px-25 pb-25 type-body-md text-ink-secondary whitespace-pre-line">
                    {step.transcript}
                  </div>
                )}
              </section>
            ) : null}
          </div>

          {/* Sticky bottom nav — inverse bar matching the rest of the
              inverse CTAs in the app. Left: icon-only previous (Arrow
              flipped). Right: justify-between Next + Arrow per the
              brand arrow-button pattern. */}
          <footer className="shrink-0 flex gap-5 bg-white p-15 text-[var(--ink-on-inverse)]">
            {i > 0 && (
              <button
                type="button"
                onClick={prev}
                aria-label="Previous step"
                className="w-50 h-50 flex items-center justify-center border-r border-[var(--ink-on-inverse)]/15 bg-black"
              >
                <span
                  aria-hidden
                  className="inline-flex w-8 rotate-180 text-white"
                >
                  <Icon name="Arrow" viewBox="0 0 10 12" />
                </span>
              </button>
            )}
            <button
              ref={nextButtonRef}
              type="button"
              onClick={next}
              className="h-50 flex-1 flex items-center justify-between px-15 md:px-25 type-body-md bg-black"
            >
              <span>{i === total - 1 ? "Done" : "Next"}</span>
              <span aria-hidden className="inline-flex w-8 text-white">
                <Icon name="Arrow" viewBox="0 0 10 12" />
              </span>
            </button>
          </footer>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
